import { Settings2Icon } from 'lucide-react';
import type { Column, HeaderContext, Table } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { isValidElement } from 'react';

import { Button } from '@r/components/ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@r/components/ui/dropdown-menu.tsx';

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
}

interface NestedObject {
  [key: string]: string | NestedObject;
}

export function DataTableViewOptions<TData>({ table }: DataTableViewOptionsProps<TData>) {
  const { t } = useTranslation();

  /**
   * Get the column header label
   * @param column The column
   */
  function getColumnHeaderLabel(column: Column<TData>): string {
    const columnHeader = column.columnDef.header;
    let columnHeaderLabel = column.id;

    // If the column header is a string, return the string
    if (typeof columnHeader === 'string') {
      columnHeaderLabel = columnHeader;
    }
    // If the column header is a function, call the function and get the label
    else if (columnHeader && typeof columnHeader === 'function') {
      const mockColumn = { column: {} } as HeaderContext<TData, unknown>;
      const header = columnHeader(mockColumn);

      if (typeof header === 'string') {
        columnHeaderLabel = header;
      } else if (isValidElement(header) && 'props' in header) {
        const props = header.props as NestedObject;

        if ('title' in props && typeof props.title === 'string') {
          columnHeaderLabel = props.title;
        }
      }
    }

    return columnHeaderLabel;
  }

  const hideableColumns = table
    .getAllColumns()
    .filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide());

  // Rien à afficher/masquer (ex. colonnes en `enableHiding: false`) : pas de menu vide.
  if (hideableColumns.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          rounded="full"
          aria-label={t('datatable.tool.toggle.columns.label')}
          variant="outline"
          className="hidden ml-auto h-8 lg:flex"
        >
          <Settings2Icon className="mr-2 size-4" />
          {t('datatable.tool.view.label')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuLabel>{t('datatable.tool.toggle.columns.label')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hideableColumns.map((column) => {
          return (
            <DropdownMenuCheckboxItem
              key={column.id}
              className="capitalize"
              checked={column.getIsVisible()}
              onCheckedChange={(value) => column.toggleVisibility(value)}
            >
              <span className="truncate">{getColumnHeaderLabel(column)}</span>
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
